import { useCallback, useRef, useState } from 'react';

interface UseApiCallOptions {
  debounceMs?: number;
  skipDuplicates?: boolean;
}

interface ApiCallState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to manage API calls with debouncing and duplicate prevention
 */
export function useApiCall<T>(
  apiFunction: (...args: any[]) => Promise<T>,
  options: UseApiCallOptions = {}
) {
  const { debounceMs = 300, skipDuplicates = true } = options;
  
  const [state, setState] = useState<ApiCallState<T>>({
    data: null,
    loading: false,
    error: null,
  });
  
  const timeoutRef = useRef<NodeJS.Timeout>();
  const currentCallRef = useRef<Promise<T> | null>(null);
  const lastArgsRef = useRef<string>('');

  const execute = useCallback(
    (...args: any[]) => {
      // Serialize args for duplicate comparison
      const argsKey = JSON.stringify(args);
      
      // Skip if it's the same call and skipDuplicates is active
      if (skipDuplicates && argsKey === lastArgsRef.current && currentCallRef.current) {
        return currentCallRef.current;
      }
      
      // Clear timeout precedente
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      return new Promise<T>((resolve, reject) => {
        timeoutRef.current = setTimeout(async () => {
          // If there's already a call in progress with the same parameters, wait for that one
          if (skipDuplicates && currentCallRef.current && argsKey === lastArgsRef.current) {
            try {
              const result = await currentCallRef.current;
              resolve(result);
            } catch (error) {
              reject(error);
            }
            return;
          }
          
          setState(prev => ({ ...prev, loading: true, error: null }));
          lastArgsRef.current = argsKey;
          
          const apiCall = apiFunction(...args);
          currentCallRef.current = apiCall;
          
          try {
            const result = await apiCall;
            setState({ data: result, loading: false, error: null });
            resolve(result);
          } catch (error) {
            const err = error as Error;
            setState(prev => ({ ...prev, loading: false, error: err }));
            reject(error);
          } finally {
            currentCallRef.current = null;
          }
        }, debounceMs);
      });
    },
    [apiFunction, debounceMs, skipDuplicates]
  );

  const reset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    currentCallRef.current = null;
    lastArgsRef.current = '';
    setState({ data: null, loading: false, error: null });
  }, []);

  return {
    ...state,
    execute,
    reset,
  };
}